// src/utils/offlineStore.js
//
// OFFLINE LISTENING, the storage layer
//
// What this is for: keeping the actual audio for a track on the person's
// device so it plays with no network at all. That is a different thing from
// the existing Downloads feature, which hands the browser an .mp3 and puts a
// file in the phone's Downloads folder, outside the app, invisible to the
// player, and no use for listening in Feelz Machine on a plane.
//
//
// WHY IndexedDB AND NOT THE CACHE API
//
// This is the decision that matters most, and getting it wrong would look
// like the feature working perfectly and then losing everybody's music at a
// random moment.
//
// netlify.toml rewrites the service worker's cache name on every single build:
//
//   sed -i "s/feelz-v[0-9]*/feelz-$(date +%Y%m%d%H%M)/g" public/service-worker.js
//
// and the worker's activate handler deletes every cache whose name is not the
// current one. So anything stored in a Cache API bucket is destroyed by the
// next deploy. Music somebody saved for a flight would vanish because an
// unrelated CSS fix went out. IndexedDB is untouched by that cleanup, which
// makes it the only correct home for this.
//
// It is also the better fit anyway: one place holds the audio, the artwork and
// the metadata, so a saved track can be listed, played and shown with a cover
// while completely offline, with no rows read from Supabase.
//
//
// WHY THERE IS A LEASE AND NOT JUST A FILE
//
// Offline entitlement cannot be checked at play time, there is no network,
// that is the entire point. So the check happens once, at save time, on the
// server (netlify/functions/get-offline-url.js), and the answer is written
// next to the audio as an expiry date. Playing offline re-reads that date
// locally; reconnecting renews it silently.
//
// Thirty days is the industry-standard window and it is what the server
// returns today. Without an expiry, one Fan Pro month would buy a permanent
// library, which is a different product.

import { cachedStreamSrc, resolveStreamSrc, PRIVATE_AUDIO } from './streamUrl';

const DB_NAME    = 'feelz-offline';
const DB_VERSION = 1;

const STORE_META  = 'meta';   // one small record per saved track
const STORE_AUDIO = 'audio';  // the audio blob, kept apart so listing is cheap
const STORE_COVER = 'cover';  // artwork blob, so covers render with no network

// The virtual path the service worker answers from IndexedDB. Same origin, so
// the audio element treats it as an ordinary file and range requests work,
// which is what makes seeking and scrubbing behave normally.
export const OFFLINE_AUDIO_PREFIX = '/offline-audio/';

// ── Opening ───────────────────────────────────────────────────────────────────

let dbPromise = null;

export function openOfflineDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('offline_unsupported'));
      return;
    }

    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(err);
      return;
    }

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_META)) {
        const meta = db.createObjectStore(STORE_META, { keyPath: 'trackId' });
        meta.createIndex('savedAt', 'savedAt');
      }
      if (!db.objectStoreNames.contains(STORE_AUDIO)) {
        db.createObjectStore(STORE_AUDIO, { keyPath: 'trackId' });
      }
      if (!db.objectStoreNames.contains(STORE_COVER)) {
        db.createObjectStore(STORE_COVER, { keyPath: 'trackId' });
      }
    };

    req.onsuccess = () => {
      const db = req.result;
      // A tab left open through an upgrade would otherwise wedge the next one.
      db.onversionchange = () => { try { db.close(); } catch {} dbPromise = null; };
      resolve(db);
    };

    req.onerror   = () => { dbPromise = null; reject(req.error || new Error('idb_open_failed')); };
    req.onblocked = () => { dbPromise = null; reject(new Error('idb_blocked')); };
  });

  return dbPromise;
}

function tx(db, stores, mode) {
  return db.transaction(stores, mode);
}

function done(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror    = () => reject(transaction.error);
    transaction.onabort    = () => reject(transaction.error || new Error('idb_aborted'));
  });
}

function reqAsPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror   = () => reject(request.error);
  });
}

// ── Leases ────────────────────────────────────────────────────────────────────

export function leaseState(meta) {
  if (!meta) return 'missing';
  if (!meta.expiresAt) return 'ok';            // shouldn't happen; fail open
  const ms = Date.parse(meta.expiresAt);
  if (!Number.isFinite(ms)) return 'ok';
  const left = ms - Date.now();
  if (left <= 0) return 'expired';
  if (left < 5 * 86400000) return 'expiring';  // under five days
  return 'ok';
}

export function daysLeft(meta) {
  const ms = Date.parse(meta?.expiresAt || '');
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.ceil((ms - Date.now()) / 86400000));
}

// ── Reading ───────────────────────────────────────────────────────────────────

export async function listOffline() {
  const db = await openOfflineDb();
  const t  = tx(db, [STORE_META], 'readonly');
  const all = await reqAsPromise(t.objectStore(STORE_META).getAll());
  return (all || []).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
}

export async function getOfflineMeta(trackId) {
  if (!trackId) return null;
  const db = await openOfflineDb();
  const t  = tx(db, [STORE_META], 'readonly');
  return (await reqAsPromise(t.objectStore(STORE_META).get(trackId))) || null;
}

export async function isSavedOffline(trackId) {
  const meta = await getOfflineMeta(trackId);
  return !!meta && leaseState(meta) !== 'expired';
}

export async function getOfflineAudioBlob(trackId) {
  const db = await openOfflineDb();
  const t  = tx(db, [STORE_AUDIO], 'readonly');
  const rec = await reqAsPromise(t.objectStore(STORE_AUDIO).get(trackId));
  return rec?.blob || null;
}

export async function getOfflineCoverBlob(trackId) {
  const db = await openOfflineDb();
  const t  = tx(db, [STORE_COVER], 'readonly');
  const rec = await reqAsPromise(t.objectStore(STORE_COVER).get(trackId));
  return rec?.blob || null;
}

export async function offlineUsage() {
  const items = await listOffline();
  const bytes = items.reduce((sum, m) => sum + (m.bytes || 0), 0);

  // The browser's own view of the origin's allowance. Purely informational , 
  // never gate a save on it, because the numbers are deliberately fuzzy and a
  // wrong refusal is worse than a failed write we can report.
  let quota = null;
  try {
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      quota = est?.quota || null;
    }
  } catch { /* not available, not important */ }

  return { count: items.length, bytes, quota };
}

// ── Writing ───────────────────────────────────────────────────────────────────

// Trimmed to what the offline library and the player actually read, so a saved
// track renders and plays with zero Supabase queries. Everything else about a
// track can be fetched when there is a network again.
function snapshotTrack(track, extra) {
  return {
    trackId:     track.id,
    title:       track.title || 'Untitled',
    slug:        track.slug || null,
    artistId:    track.artist_id || track.artist?.id || null,
    artistName:  track.artist?.artist_name || track.artist_name || 'Unknown artist',
    artistSlug:  track.artist?.slug || track.artist_slug || null,
    coverUrl:    track.cover_artwork_url || null,
    duration:    track.duration || null,
    fileUrl:     track.file_url || null,   // for the online path and re-saves
    isBeat:      !!track.is_beat,
    albumId:     track.album_id || null,
    ...extra,
  };
}

/**
 * saveTrackOffline
 *
 * The whole save, start to finish: ask the server whether this person may keep
 * this track, stream the bytes down with progress, store audio + artwork +
 * metadata in one transaction.
 *
 * The transaction is the part worth noticing. Audio and metadata are written
 * together or not at all, so there is never a metadata row promising a track
 * whose audio failed to land, which would show as a song in the library that
 * silently refuses to play.
 *
 * onProgress is called with 0..1, or null when the response has no
 * Content-Length and there is nothing honest to report.
 */
export async function saveTrackOffline(track, { authToken, onProgress, signal, userId = null } = {}) {
  if (!track?.id) throw new Error('no_track');
  if (!authToken) throw new Error('not_authenticated');

  // 1. Entitlement + a signed URL. This is the only moment the rules are
  //    checked, so it happens on the server, not here.
  const res = await fetch('/.netlify/functions/get-offline-url', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ trackId: track.id }),
    signal,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // The code alone is not enough to write a decent message. A pre-order
    // refusal needs the release date and needs to know whether this person
    // bought it, because telling a buyer the same thing you tell a stranger
    // is the part that reads as the platform having forgotten they paid.
    // Carried on the Error rather than thrown as an object so every existing
    // `err.message` check keeps working untouched.
    const e = new Error(body.error || `offline_url_failed_${res.status}`);
    e.details = body;
    throw e;
  }

  const { signedUrl, expiresAt, mimeType } = await res.json();
  if (!signedUrl) throw new Error('offline_url_missing');

  // 2. The audio itself, with progress where the server tells us the size.
  const audioRes = await fetch(signedUrl, { signal });
  if (!audioRes.ok) throw new Error('audio_fetch_failed');

  const total = Number(audioRes.headers.get('content-length')) || 0;
  let blob;

  if (audioRes.body && typeof audioRes.body.getReader === 'function' && total > 0) {
    const reader = audioRes.body.getReader();
    const chunks = [];
    let received = 0;
    for (;;) {
      const { done: finished, value } = await reader.read();
      if (finished) break;
      chunks.push(value);
      received += value.length;
      if (onProgress) onProgress(Math.min(1, received / total));
    }
    blob = new Blob(chunks, { type: audioRes.headers.get('content-type') || mimeType || 'audio/mpeg' });
  } else {
    // No stream or no length, Safari and some webviews. Still works, just
    // without a progress bar, which is better than refusing to save.
    if (onProgress) onProgress(null);
    blob = await audioRes.blob();
  }

  if (!blob.size) throw new Error('audio_empty');

  // 3. Artwork. Best effort: a missing cover is a cosmetic problem, and
  //    failing the whole save over it would be absurd.
  let coverBlob = null;
  if (track.cover_artwork_url) {
    try {
      const c = await fetch(track.cover_artwork_url, { signal });
      if (c.ok) coverBlob = await c.blob();
    } catch { /* cosmetic */ }
  }

  // 4. One transaction for all of it.
  const db = await openOfflineDb();
  const t  = tx(db, [STORE_META, STORE_AUDIO, STORE_COVER], 'readwrite');

  const meta = snapshotTrack(track, {
    bytes:     blob.size,
    mimeType:  blob.type || 'audio/mpeg',
    savedAt:   Date.now(),
    expiresAt: expiresAt || null,
    // WHOSE DOWNLOAD THIS IS.
    //
    // IndexedDB belongs to the ORIGIN, not to the account, the same way
    // localStorage does and for the same reason. So every download anybody has
    // ever saved in this browser is visible to whoever signs in next, and
    // renewLeases was asking the server to renew all of them on their behalf.
    // The server correctly answered 403, because they are not entitled to
    // somebody else's music, and that is the pair of 403s on every brand new
    // account: two tracks a previous account had saved in the same browser.
    //
    // Stamping the owner lets the renewal skip what is not theirs instead of
    // asking about it. The file is left alone, because it still belongs to the
    // person who saved it and they may well sign back in.
    userId: userId || null,
  });

  t.objectStore(STORE_META).put(meta);
  t.objectStore(STORE_AUDIO).put({ trackId: track.id, blob });
  if (coverBlob) t.objectStore(STORE_COVER).put({ trackId: track.id, blob: coverBlob });

  try {
    await done(t);
  } catch (err) {
    // QuotaExceededError is the common one and deserves its own name so the
    // UI can say "your phone is full" rather than "something went wrong".
    if (err?.name === 'QuotaExceededError') throw new Error('device_storage_full');
    throw err;
  }

  if (onProgress) onProgress(1);
  return meta;
}

export async function removeOffline(trackId) {
  releaseObjectUrl(trackId);
  const db = await openOfflineDb();
  const t  = tx(db, [STORE_META, STORE_AUDIO, STORE_COVER], 'readwrite');
  t.objectStore(STORE_META).delete(trackId);
  t.objectStore(STORE_AUDIO).delete(trackId);
  t.objectStore(STORE_COVER).delete(trackId);
  await done(t);
}

export async function clearOffline() {
  releaseAllObjectUrls();
  const db = await openOfflineDb();
  const t  = tx(db, [STORE_META, STORE_AUDIO, STORE_COVER], 'readwrite');
  t.objectStore(STORE_META).clear();
  t.objectStore(STORE_AUDIO).clear();
  t.objectStore(STORE_COVER).clear();
  await done(t);
}

/**
 * renewLeases
 *
 * Called when the app has a network again. Re-asks the server about every
 * saved track and writes the new expiry, without re-downloading a byte.
 *
 * The two kinds of refusal are treated differently on purpose:
 *
 *   410 / 404, the track itself is gone: the artist unpublished it, or it was
 *               deleted. The local copy is removed, because there is nothing
 *               left for it to be a copy of.
 *   403      , the person is not entitled RIGHT NOW: Fan Pro lapsed, or a
 *               paid track's purchase no longer covers it. The copy is LEFT
 *               ALONE and simply stops renewing, so it runs out its remaining
 *               lease instead of vanishing the moment a card is declined.
 *
 * Deleting on 403 would mean a subscription lapsing mid-holiday wipes
 * somebody's music with no warning. Letting the lease expire gives them up to
 * thirty days and a visible countdown, which is the difference between a
 * feature that ends and a feature that punishes.
 */
const DENY_RETRY_MS = 7 * 24 * 60 * 60 * 1000;

// A lease runs 30 days. Renewing one that still has most of that left is pure
// cost, so only the ones inside this window are asked about.
const RENEW_WHEN_WITHIN_MS = 7 * 24 * 60 * 60 * 1000;

// How many renewals are in flight at once. The server call is a cold Netlify
// function taking 1.2 to 2.9 seconds in practice, so this is the difference
// between a slow page and an unusable one.
const RENEW_CONCURRENCY = 4;

export async function renewLeases(authToken, userId = null) {
  if (!authToken) return { renewed: 0, revoked: 0, lapsed: 0, failed: 0, skipped: 0 };

  const items = await listOffline();
  let renewed = 0, revoked = 0, lapsed = 0, failed = 0, skipped = 0;

  // ── WHY THIS IS NO LONGER A SERIAL LOOP OVER EVERYTHING ────────────────────
  //
  // It used to be `for (const meta of items)` with an await inside, so one
  // request went out, finished, and then the next started. Measured on the
  // live site, each call takes between 1.2 and 2.9 seconds, because it is a
  // cold Netlify function. MAX_OFFLINE_TRACKS in get-offline-url.js is 500.
  //
  // So somebody with a full offline library was making 500 requests, one after
  // another, at roughly a second and a half each. That is over ten minutes of
  // continuous network activity, and it started again on every page load,
  // because the once-an-hour guard in OfflineContext lives in a ref and a ref
  // does not survive a reload. With two saved tracks it already accounted for
  // the slowest requests on every page I measured.
  //
  // Two changes fix it, and the first one matters more than the second.
  //
  // ONLY WHAT IS ACTUALLY DUE. A lease lasts 30 days. Renewing one with 29
  // days left achieves nothing, so an item is only asked about inside the last
  // week of its life. On a normal load that is almost nothing, whatever the
  // library size, which is the part that makes this scale.
  //
  // THEN IN PARALLEL, four at a time. For the handful that are genuinely due,
  // the wall-clock cost is now a quarter of what it was, and the cap keeps it
  // from looking like an attack on your own function.
  const due = [];
  for (const meta of items) {
    // Somebody else's download, in a browser this account also uses. Not asked
    // about, not touched. See the note on userId in saveTrackOffline: without
    // this, every account signing in on a shared or previously-used browser
    // sent one request per foreign track and collected a 403 for each.
    //
    // Items with no userId were saved before this existed. They are renewed as
    // before and get stamped below if the server says this person is entitled,
    // which is the only evidence available that they are the owner.
    if (userId && meta.userId && meta.userId !== userId) { skipped++; continue; }

    // AND THE ONES ALREADY IN THE BROWSER.
    //
    // The stamp above only exists on downloads saved after it shipped, so on
    // its own it does nothing for anything already sitting in IndexedDB, which
    // on any browser that has been used before is all of them. Those are the
    // 403s that kept appearing.
    //
    // An unstamped item that comes back 403 is one of two things and the
    // response cannot tell them apart: somebody else's download in a shared
    // browser, or this person's own download with a lapsed entitlement. So
    // neither is assumed. The refusal is recorded against this user and this
    // track, and not asked again for a week.
    //
    // A week rather than never, because the second case comes back: somebody
    // who resubscribes gets their own music renewing again without having to
    // know any of this happened. Nothing is deleted either way, which is the
    // same reasoning as the 403 branch below.
    if (userId && meta.deniedFor === userId
        && meta.deniedAt && (Date.now() - meta.deniedAt) < DENY_RETRY_MS) {
      skipped++;
      continue;
    }

    // Not due yet. An item with no expiry at all IS asked about, because an
    // unknown lease is the one case where the server's answer is the only way
    // to find out what is going on.
    if (meta.expiresAt) {
      const left = new Date(meta.expiresAt).getTime() - Date.now();
      if (Number.isFinite(left) && left > RENEW_WHEN_WITHIN_MS) { skipped++; continue; }
    }

    due.push(meta);
  }

  const renewOne = async (meta) => {
    try {
      const res = await fetch('/.netlify/functions/get-offline-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        // renew: the server checks entitlement and returns an expiry without
        // signing a URL, so this is cheap and downloads nothing.
        body: JSON.stringify({ trackId: meta.trackId, renew: true }),
      });

      // The track is gone from the platform. Nothing to keep.
      if (res.status === 404 || res.status === 410) {
        await removeOffline(meta.trackId);
        revoked++;
        return;
      }

      // Not entitled at this moment. Leave the existing lease to run out ,
      // see the note above on why this is not a delete.
      if (res.status === 403) {
        lapsed++;
        // Remembered, so this account stops asking the same question on every
        // single page load. See the note on DENY_RETRY_MS above.
        if (userId) {
          try {
            const dbD = await openOfflineDb();
            const tD  = tx(dbD, [STORE_META], 'readwrite');
            const stD = tD.objectStore(STORE_META);
            const cur = await reqAsPromise(stD.get(meta.trackId));
            if (cur) stD.put({ ...cur, deniedFor: userId, deniedAt: Date.now() });
            await done(tD);
          } catch { /* the console line is the only cost of failing here */ }
        }
        return;
      }

      if (!res.ok) { failed++; return; }

      const { expiresAt } = await res.json();
      if (!expiresAt) { failed++; return; }

      const db = await openOfflineDb();
      const t  = tx(db, [STORE_META], 'readwrite');
      const store = t.objectStore(STORE_META);
      const current = await reqAsPromise(store.get(meta.trackId));
      // Stamped on the way past. A successful renewal is the server saying this
      // person is entitled to this track, which is the evidence that an
      // unstamped legacy item is theirs.
      // deniedFor is cleared on the way past: the server has just said yes, so
      // whatever it said before is no longer true.
      if (current) store.put({
        ...current,
        expiresAt,
        userId: current.userId || userId || null,
        deniedFor: null,
        deniedAt: null,
      });
      await done(t);
      renewed++;
    } catch {
      // Offline again mid-renew, or a transient failure. Leaving the existing
      // expiry alone is right: nothing is lost, and the next reconnect retries.
      failed++;
    }
  };

  // A fixed number of workers pulling from one shared list. Simpler than
  // chunking into batches, and it keeps all four busy instead of waiting for
  // the slowest member of each batch.
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(RENEW_CONCURRENCY, due.length) }, async () => {
      while (next < due.length) {
        const meta = due[next++];
        await renewOne(meta);
      }
    })
  );

  return { renewed, revoked, lapsed, failed, skipped };
}

// ── Resolving a playable src ───────────────────────────────────────────────────

// Two ways to play a stored blob, in order of preference.
//
// 1. Through the service worker, at /offline-audio/<id>. The audio element
//    sees a normal same-origin HTTP response with Accept-Ranges, so seeking,
//    scrubbing and duration all work exactly as they do online.
//
// 2. A blob: object URL. Used when no worker is controlling the page yet , 
//    a first visit before the worker takes over, or a browser with service
//    workers disabled. Playback works; seeking in a blob URL is less reliable
//    on older iOS, which is why it is the fallback and not the default.

const objectUrls = new Map(); // trackId -> blob: URL
const MAX_OBJECT_URLS = 4;    // crossfade needs two live at once

function releaseObjectUrl(trackId) {
  const url = objectUrls.get(trackId);
  if (url) {
    try { URL.revokeObjectURL(url); } catch {}
    objectUrls.delete(trackId);
  }
}

function releaseAllObjectUrls() {
  for (const id of Array.from(objectUrls.keys())) releaseObjectUrl(id);
}

export function serviceWorkerCanServe() {
  return typeof navigator !== 'undefined'
      && 'serviceWorker' in navigator
      && !!navigator.serviceWorker.controller;
}

export function offlineAudioPath(trackId) {
  return `${OFFLINE_AUDIO_PREFIX}${encodeURIComponent(trackId)}`;
}

/**
 * offlineSrcFor
 *
 * Returns a src the audio element can use, or null if this track is not
 * usable offline. Null is the signal to fall back to the streaming URL, this
 * function never throws into the playback path.
 */
export async function offlineSrcFor(trackId) {
  if (!trackId) return null;

  let meta;
  try {
    meta = await getOfflineMeta(trackId);
  } catch {
    return null;
  }
  if (!meta) return null;
  if (leaseState(meta) === 'expired') return null;

  if (serviceWorkerCanServe()) return offlineAudioPath(trackId);

  const existing = objectUrls.get(trackId);
  if (existing) return existing;

  let blob;
  try {
    blob = await getOfflineAudioBlob(trackId);
  } catch {
    return null;
  }
  if (!blob) return null;

  if (objectUrls.size >= MAX_OBJECT_URLS) {
    releaseObjectUrl(objectUrls.keys().next().value);
  }

  const url = URL.createObjectURL(blob);
  objectUrls.set(trackId, url);
  return url;
}

/**
 * offlineSrcSync / playbackSrc
 *
 * The player sets audio.src synchronously, playTrack, the crossfade and
 * jumpToIndex all do, and that is worth preserving: making every online play
 * wait on an IndexedDB round trip to discover it is not saved would slow down
 * the common case to serve the rare one.
 *
 * So the fast path is entirely synchronous. When a service worker is
 * controlling the page, the local URL is just a string built from the track
 * id, /offline-audio/<id>, and needs no storage read at all, because the
 * worker does the reading when the audio element requests it.
 *
 * The only case that cannot be answered synchronously is a saved track with no
 * worker controlling the page, where a blob: URL has to be created from the
 * stored bytes. offlineSrcSync returns null there and the caller falls back to
 * streaming; PlayerContext then retries asynchronously via offlineSrcFor,
 * which matters when that state coincides with actually being offline.
 */
export function offlineSrcSync(trackId) {
  if (!trackId) return null;
  if (!isSavedOfflineSync(trackId)) return null;
  if (serviceWorkerCanServe()) return offlineAudioPath(trackId);
  return objectUrls.get(trackId) || null;
}

export function playbackSrc(track) {
  // Order matters, and it is: the copy on this device, then a signed
  // streaming URL if one is already cached, then the stored file_url.
  //
  // cachedStreamSrc is a synchronous cache read and returns null unless
  // REACT_APP_PRIVATE_AUDIO is on, so with the flag off this is byte for byte
  // the old behaviour.
  //
  // With the flag ON, the file_url fallback is deliberately NOT taken. It used
  // to be, and PlayerContext swapped the signed URL in a moment later. That
  // upgrade is invisible while the bucket is public and fatal once it is not:
  // on a cold cache the element gets a URL that no longer resolves, errors,
  // and can be dead before the swap lands. Returning null here instead is what
  // lets assignPlaybackSrc below wait for a real URL. This is the one change
  // the bucket flip was waiting on.
  //
  // The one exception is a saved copy of a file the track no longer points at.
  // Re-uploading a master leaves every offline copy pointing at the old audio,
  // and preferring it means the artist who just replaced the file is the one
  // person guaranteed to keep hearing the old version.
  const fallback = PRIVATE_AUDIO ? null : (track?.file_url || null);
  if (offlineCopyIsStale(track)) {
    return cachedStreamSrc(track?.id) || fallback;
  }
  return offlineSrcSync(track?.id) || cachedStreamSrc(track?.id) || fallback;
}

/**
 * Assign a playable source to an element, waiting for one if it has to.
 *
 * The fast path is unchanged and synchronous in effect: if a local copy or an
 * already-signed URL is there, it is assigned before this function yields, so
 * a warm cache behaves exactly as it did before. Only a cold cache under
 * PRIVATE_AUDIO awaits anything, and that is the case that used to assign a
 * dead public URL.
 *
 * Returns the URL assigned, or null if nothing could be resolved.
 */
export async function assignPlaybackSrc(audio, track, { load = true } = {}) {
  if (!audio || !track?.id) return null;

  const immediate = playbackSrc(track);
  if (immediate) {
    audio.src = immediate;
    if (load) audio.load();
    return immediate;
  }

  // PRIVATE_AUDIO is on and nothing is cached yet.
  //
  // The comment here used to say resolveStreamSrc falls back to file_url, so a
  // signing outage degraded to the old behaviour rather than to silence. That
  // stopped being true the day the bucket went private: file_url is a
  // public-object link that answers 400, so on a signing failure the only
  // honest answer is null, and that is what comes back now. Nothing is
  // assigned and the caller is told, instead of an element being handed a URL
  // that fails to load and reads as a dead file.
  const signed = await resolveStreamSrc(track);
  if (!signed) return null;

  // The listener may have skipped on while the signing request was in flight.
  // Assigning now would yank them back to a track they already left.
  const stamped = audio.dataset?.feelzTrackId;
  if (stamped && stamped !== String(track.id)) return null;

  audio.src = signed;
  if (load) audio.load();
  return signed;
}

export function isOfflineSrc(src) {
  return typeof src === 'string'
      && (src.startsWith('blob:') || src.includes(OFFLINE_AUDIO_PREFIX));
}

/**
 * A synchronous view of what is saved, for render paths that cannot await.
 *
 * Kept warm by useOfflineLibrary. Read-only and best-effort: an empty map
 * means "not known yet", never "definitely not saved", so nothing should
 * block on it.
 *
 * It holds the file_url each copy was saved from as well as the id, which is
 * what makes the staleness check below possible without a storage read.
 */
let savedCache = new Map();   // trackId -> fileUrl at the time it was saved

/**
 * Accepts the meta records, or a plain list of ids for any caller that still
 * has only ids. Ids alone mean the file_url is unknown, and an unknown
 * file_url is never treated as stale: the saved copy still plays, exactly as
 * it did before this existed.
 */
export function primeSavedIds(list) {
  const next = new Map();
  for (const entry of list || []) {
    if (entry && typeof entry === 'object') next.set(entry.trackId, entry.fileUrl || null);
    else next.set(entry, null);
  }
  savedCache = next;
}

export function savedIdsSnapshot() {
  return new Set(savedCache.keys());
}

export function isSavedOfflineSync(trackId) {
  return savedCache.has(trackId);
}

/**
 * offlineCopyIsStale
 *
 * True when this device holds a saved copy that was taken from a DIFFERENT
 * file than the track now points at, AND there is a network to fetch the
 * current one from.
 *
 * The network condition is not a detail, it is the whole reason this function
 * is safe. Bypassing the saved copy means streaming instead, and streaming
 * with no signal plays nothing at all. Somebody on a plane holding last
 * month's master should hear last month's master, not silence, so when the
 * browser says there is no connection the saved copy wins no matter how old
 * it is. An old master beats no master.
 *
 * navigator.onLine is only trustworthy in one direction: false really does
 * mean no network, while true can still mean a captive portal or a dead
 * connection. That asymmetry happens to point the right way here. A false
 * negative leaves the old behaviour exactly as it was, which is what it did
 * before any of this existed.
 *
 * This is the case an artist hits after re-uploading a master. Replacing the
 * audio writes a brand new object: uploadFile in src/pages/TrackUploadPanel.js
 * names every upload `${Date.now()}-${random}.${ext}` and never overwrites, so
 * a replaced track always has a file_url it has never had before. Anyone
 * holding an offline copy, the artist very much included, kept playing the old
 * master forever, because playbackSrc preferred the local copy and nothing
 * ever compared the two.
 *
 * Deliberately conservative. It only reports stale when BOTH urls are known
 * and they differ. Unknown on either side means play what is saved, because
 * the entire point of an offline copy is that it works when nothing else does,
 * and refusing to play it on a guess would be a far worse bug than playing a
 * slightly old master.
 */
export function offlineCopyIsStale(track) {
  if (!track?.id) return false;
  if (!savedCache.has(track.id)) return false;
  // No network, no bypass. See the note above.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  const savedFrom = savedCache.get(track.id);
  const current   = track.file_url || null;
  if (!savedFrom || !current) return false;
  return savedFrom !== current;
}